import React from "react";

const CDN_TEAM_LOGO = (id) => (id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined);
const faceOf = (pid) => (pid ? `https://media.api-sports.io/football/players/${pid}.png` : undefined);
const cx = (...a) => a.filter(Boolean).join(" ");
const IconCaptain = () => <span className="lu-badge lu-badge--cap">C</span>;

const POS_LABELS = {
  G: "Goalkeeper",
  D: "Defender",
  M: "Midfielder",
  F: "Forward",
};

const posLabel = (p) => POS_LABELS[p?.toUpperCase?.()] || p || "";

function parseFormationArray(formation) {
  if (!formation) return [];
  return String(formation)
    .split("-")
    .map((n) => Math.max(1, Number(n) || 1));
}

function parseGrid(grid) {
  const [r, c] = String(grid || "").split(":").map((x) => Number(x));
  if (!Number.isFinite(r) || !Number.isFinite(c)) return { row: null, col: null };
  return { row: r, col: c };
}

function columnsInRow(rowIndex, formationArr) {
  if (rowIndex === 1) return 1;
  const idx = rowIndex - 2; 
  return formationArr[idx] ?? 1;
}

function orientedHalfPlacement({ grid, side, formation }) {
  const fa = parseFormationArray(formation);
  const totalLines = 1 + fa.length;

  const { row: rawRow, col: rawCol } = parseGrid(grid);
  const row = Math.min(Math.max(rawRow || 1, 1), totalLines);
  const cols = columnsInRow(row, fa);
  const col = Math.min(Math.max(rawCol || Math.ceil(cols / 2), 1), cols);

  const lineProgress = totalLines > 1 ? (row - 1) / (totalLines - 1) : 0;

  let xPct;
  if (side === "home") {
    xPct = 4 + lineProgress * 42;
  } else {
    xPct = 96 - lineProgress * 42;
  }

  const yTop = 6, yBottom = 94;
  const usableY = yBottom - yTop;
  let yPct;
  if (cols <= 1) {
    yPct = 50;
  } else {
    const step = 1 / (cols + 1);
    const yNorm = step * col;             
    yPct = yTop + yNorm * usableY;        
  }

  return { top: `${yPct}%`, left: `${xPct}%` };
}

function PlayerRow({ right = false, data }) {
  const p = data?.player || {};
  const num = p.number;
  const pid = p.id;
  const name = p.name;
  const pos = posLabel(p.pos);
  const isCaptain = !!p.captain;

  const rateClass =
    p.pos === "G" ? "is-gk" :
      p.pos === "D" ? "is-def" :
        p.pos === "M" ? "is-mid" :
          p.pos === "F" ? "is-fwd" :
            "";

  return (
    <div className={cx("lu-row", right && "is-right")}>
      {!right && <span className="lu-num">{num ?? ""}</span>}
      <div className="lu-facewrap">
        {pid ? <img className="lu-face" style={{ width: '40px' }} src={faceOf(pid)} alt="" /> : <span className="lu-face lu-face--ph" />}
      </div>
      <div className="lu-name">
        <span className="lu-n">{name}</span>
        <div className="lu-note">
          {pos}
          {isCaptain ? " · Captain" : ""}
          {p.grid ? ` · ${p.grid}` : ""}
        </div>
        <div className="lu-flags">
          {isCaptain && <IconCaptain />}
          {/* <IconSub dir="in" /> / <IconSub dir="out" /> / <IconYC/> <IconRC/> <IconSY/> */}
        </div>
      </div>
      <div className={`lu-rate ${rateClass}`}>{p.pos}</div>
    </div>
  );
}

export default function MatchLineups({ fixture, lineups }) {
  const home = lineups[0] ?? fixture?.teams?.home;
  const away = lineups[1] ??  fixture?.teams?.away;

  const Pitch = () => (
    <div className="lu-pitch-outer">
      <div className="lu-pitch">
        <div className="lu-midline" />
        <div className="lu-circle" />
        <div className="lu-box lu-box--left" />
        <div className="lu-box lu-box--right" />
        <div className="lu-box-small lu-box-small--left" />
        <div className="lu-box-small lu-box-small--right" />
  
        {home?.startXI?.map((x) => {
          const p = x?.player;
          const st = orientedHalfPlacement({ grid: p?.grid, side: "home", formation: home?.formation });
          return (
            <div className="lu-dot" key={`h-${p?.id || p?.name}`} style={st}>
              <img className="lu-dot-face" src={faceOf(p?.id)} alt="" />
              <div className="lu-dot-name">{p?.name}</div>
            </div>
          );
        })}
  
        {away?.startXI?.map((x) => {
          const p = x?.player;
          const st = orientedHalfPlacement({ grid: p?.grid, side: "away", formation: away?.formation });
          return (
            <div className="lu-dot lu-dot--away" key={`a-${p?.id || p?.name}`} style={st}>
              <img className="lu-dot-face" src={faceOf(p?.id)} alt="" />
              <div className="lu-dot-name">{p?.name}</div>
            </div>
          );
        })}
  
        <div className="lu-form lu-form--left">{home?.formation}</div>
        <div className="lu-form lu-form--right">{away?.formation}</div>
      </div>
    </div>
  );  

  const LeftList = ({ title, list }) => (
    <div className="lu-col">
      <h4 className="lu-h4"><img style={{ width: '30px', marginLeft: 5 }} src={home?.logo || home?.team?.logo || CDN_TEAM_LOGO(home?.id || home?.team?.id)} alt="" />&nbsp; &nbsp;{title}</h4>
      {list?.map((x) => (
        <PlayerRow key={x?.player?.id || x?.player?.name} data={x} />
      ))}
    </div>
  );
  const RightList = ({ title, list }) => (
    <div className="lu-col">
      <h4 className="lu-h4"><img style={{ width: '30px', marginLeft: 5 }} src={away?.logo || away?.team?.logo || CDN_TEAM_LOGO(away?.id || away?.team?.id)} alt="" />&nbsp; &nbsp;{title}</h4>
      {list?.map((x) => (
        <PlayerRow key={x?.player?.id || x?.player?.name} data={x} />
      ))}
    </div>
  );

  return (
    <section className="px-container">
      <div className="lu-wrap">
        <style>{css}</style>

        <h5 className="section-title mb-0">
          {home?.name || home?.team?.name} vs {away?.name || away?.team?.name} — Lineups & Formation
        </h5>
        <p className="section-subtitle mb-3">
          Official starting XIs, benches, and a live formation map. Players are positioned by formation and API grid.
        </p>

        <div className="lu-head">
          <div className="lu-team">
            <img src={home?.logo || home?.team?.logo || CDN_TEAM_LOGO(home?.id || home?.team?.id)} alt="" />
            <h3>{home?.name || home?.team?.name}</h3>
          </div>
          <div className="lu-team lu-team--right">
            <h3>{away?.name || away?.team?.name}</h3>
            <img src={away?.logo || away?.team?.logo || CDN_TEAM_LOGO(away?.id || away?.team?.id)} alt="" />
          </div>
        </div>

        <Pitch />

        <div className="lu-two">
          <LeftList title="Starting XI" list={home.startXI} />
          <RightList title="Starting XI" list={away.startXI} />
        </div>

        <div className="lu-two">
          <LeftList title="Substitutes" list={home.substitutes} />
          <RightList title="Substitutes" list={away.substitutes} />
        </div>
      </div>
    </section>
  );
}

const css = `
.lu-pitch-outer {
  overflow-x: visible; /* desktop default */
  overflow-y: visible;
}
  @media (max-width: 560px) {
  .lu-pitch-outer {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    /* Optional: soften edges and show it's scrollable */
    border-radius: 12px;
    box-shadow: inset 0 0 0 1px rgba(2,6,23,.08);
    background: transparent;           /* keep your pitch bg on .lu-pitch */
  }

  /* Give the inner pitch a wider canvas so left half shows first,
     and user can scroll to the right half */
  .lu-pitch {
    min-width: 1200px;                 /* tune: 1100–1400px works well */
    height: 380px;                     /* you already set this for phones */
    margin: 0;                         /* ensure it starts at the left edge */
  }

  /* (Nice-to-have) Hide scrollbar in WebKit while preserving scroll */
  .lu-pitch-outer::-webkit-scrollbar { height: 0; }
}
.px-container { background:#f7f7f9; padding:30px; }
@media (max-width: 620px) { .px-container { padding:16px; } }
/* Stack the two lineup columns on small screens */
@media (max-width: 560px) {
  .lu-two {
    grid-template-columns: 1fr !important;   /* left then right */
    gap: 10px;
  }

  /* Header: also stack nicely on very small screens */
  .lu-head {
    grid-template-columns: 1fr 1fr !important; /* keep two columns */
  }
  .lu-team img { width:22px; height:22px; }
  .lu-team h3 { font-size:14px; }
  .lu-team--right { justify-self: end; }   /* align away team left on stack */
}


/* Ensure row layout stays consistent on both sides (no mirror) */
.lu-row.is-right { grid-template-columns: 42px 50px minmax(0,1fr) 56px !important; }
@media (max-width: 560px){
  .lu-row.is-right { grid-template-columns: 36px 50px minmax(0,1fr) 44px !important; }
}
.lu-wrap{max-width: 1380px; margin: 0 auto;}
.lu-error{padding:10px 12px;color:#991b1b;background:#fee2e2;border:1px solid #fecaca;border-radius:8px;}
.lu-skel-row{height:38px;border-radius:8px;background:linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 37%,#f1f5f9 63%);background-size:400% 100%;animation:lu-shimmer 1.2s infinite linear;margin:8px 0;}
@keyframes lu-shimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}

.lu-head{
  display:grid !important;
  grid-template-columns:minmax(0,1fr) minmax(0,1fr);
  align-items:center; gap:12px; margin-bottom:8px;
}
.lu-team{display:flex; align-items:center; gap:8px;}
.lu-team--right{justify-self:end;}
.lu-team img{width:26px;height:26px;object-fit:contain}
.lu-team h3{margin:0;font-size:18px;font-weight:800;color:#0f172a}

.lu-two{
  display:grid;
  grid-template-columns:minmax(0,1fr) minmax(0,1fr);
  gap:16px; margin-top:12px
}
@media (max-width: 520px){
  .lu-team img{width:22px;height:22px}
  .lu-team h3{font-size:16px}
  .lu-two{gap:12px}
}

.lu-h4{margin:0 0 8px;font-size:14px;font-weight:800;color:#0f172a}
.lu-col{background:#fff;border:1px solid rgba(2,6,23,.08);border-radius:10px;padding:10px}

.lu-row{
  display:grid;
  grid-template-columns:42px 28px minmax(0,1fr) 56px;
  align-items:center; gap:8px; padding:6px 4px; border-radius:8px
}
.lu-row{
  display:grid;
  grid-template-columns: 42px 50px minmax(0,1fr) 56px; /* num | avatar | text | rating */
  align-items:center; gap:8px; padding:6px 4px; border-radius:8px;
}
.lu-row:hover{background:#f8fafc}
.lu-num{color:#64748b;font-weight:800;text-align:center}
.lu-facewrap{width:40px;height:40px}
.lu-face{width:40px;height:40px;border-radius:50%;object-fit:cover;border:2px solid #10b981;background:#fff}
.lu-name{display:flex;flex-direction:column;min-width:0}
.lu-n{font-weight:700;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
/* Full name + Position shown (position is in .lu-note) */
.lu-note{font-size:12px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.lu-rate{justify-self:end;background:#f8af2e;color:#fff;font-weight:900;padding:2px 6px;border-radius:6px;min-width:36px;text-align:center}

/* Colors by position */
.lu-rate.is-gk  { background:#ef4444; } /* red */
.lu-rate.is-def { background:#3b82f6; } /* blue */
.lu-rate.is-mid { background:#22c55e; } /* green */
.lu-rate.is-fwd { background:#9333ea; } /* darker blue */
/* Compact on phones */
@media (max-width: 560px){
  .lu-row{grid-template-columns: 36px 50px minmax(0,1fr) 44px; gap:6px}
  .lu-face, .lu-facewrap{width:40px; height:40px}
  .lu-rate{min-width:40px; font-size:12px}
}
.lu-flags{display:flex;gap:6px;align-items:center;flex-wrap:wrap}
.lu-ico{display:inline-flex;width:18px;justify-content:center}
.lu-goals{display:inline-flex;align-items:center;gap:2px}
.lu-x{font-size:11px;color:#475569}
.lu-card{display:inline-block;width:12px;height:16px;border-radius:2px;box-shadow:0 1px 0 rgba(0,0,0,.1)}
.lu-card--y{background:#facc15;border:1px solid #eab308}
.lu-card--r{background:#ef4444;border:1px solid #dc2626}
.lu-cardstack{position:relative;width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center}
.lu-cardstack .lu-card{position:absolute;transform:translateX(-3px)}
.lu-cardstack .lu-card + .lu-card{transform:translateX(3px)}
.lu-sub{font-weight:900}
.lu-sub.is-in{color:#16a34a}
.lu-sub.is-out{color:#ef4444}
.lu-badge{display:inline-flex;align-items:center;justify-content:center;height:16px;min-width:16px;padding:0 4px;border-radius:999px;font-size:11px;font-weight:900;color:#0f172a;background:#e2e8f0}
.lu-badge--assist{background:#dbeafe;color:#1d4ed8}
.lu-badge--cap{background:#e9d5ff;color:#6b21a8}

/* ---------- Pitch ---------- */
.lu-pitch{
  position:relative;margin:16px 0;border:1px solid rgba(2,6,23,.08);
  border-radius:12px;height:520px;background:linear-gradient(0deg,#e8f6ea 0%,#f1fbf3 100%);
}
.lu-midline{position:absolute;left:50%;top:0%;bottom:0%;width:2px;background:rgba(2,6,23,.10);transform:translateX(-1px)}
.lu-circle{position:absolute;left:50%;top:50%;width:220px;height:220px;border:2px solid rgba(2,6,23,.10);border-radius:50%;transform:translate(-50%,-50%)}
.lu-box{position:absolute;top:20%;bottom:20%;width:150px;border:2px solid rgba(2,6,23,.10);background:transparent}
.lu-box--left{left:0%;border-left:none!important}
.lu-box--right{right:0%;border-right:none!important}

.lu-box-small{position:absolute;top:30%;bottom:30%;width:90px;border:2px solid rgba(2,6,23,.10);background:transparent}
.lu-box-small--left{left:0%;border-left:none!important}
.lu-box-small--right{right:0%;border-right:none!important}

.lu-form{position:absolute;top:8px;color:#64748b;font-weight:800;font-size:12px}
.lu-form--left{left:8px}
.lu-form--right{right:8px}

.lu-dot{
  position:absolute;transform:translate(-50%,-50%);
  display:flex;flex-direction:column;align-items:center;gap:4px
}
.lu-dot-face{width:44px;height:44px;border-radius:50%;object-fit:cover;border:3px solid #10b981;background:#fff}
.lu-dot--away .lu-dot-face{border-color:#ef4444}
.lu-dot-name{
  max-width:140px;text-align:center;font-size:12px;font-weight:700;color:#0f172a;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis
}

@media (max-width: 900px){
  .lu-dot-name{display:none}
  .lu-pitch{height:440px}
}
@media (max-width: 560px){
  .lu-row{grid-template-columns:36px 50px minmax(0,1fr) 44px; gap:6px}
  .lu-row.is-right{grid-template-columns:44px minmax(0,1fr) 24px 36px}
  .lu-face, .lu-facewrap{width:40px; height:40px}
  .lu-rate{min-width:30px; font-size:12px}
  .lu-pitch{height:380px}
}
`;
